/**
 * Logique de fusion du brain — cœur métier, sans moteur de rendu.
 *
 * Extrait de `legacy/brain.js` (calculateDropzoneValue:1131, computeTokenValues:1290).
 * Ces 260 lignes ne dépendaient ni de Pixi, ni des stores, ni du DOM : c'est de
 * l'arithmétique et du parsing de chaîne. Les isoler ici permet de les tester
 * et de refondre le rendu sans risquer de casser le scoring en silence.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * COMMENT SE LIT UN NOM DE TOKEN
 *
 * Le nom encode la forme dessinée, une lettre + une épaisseur :
 *   c = cercle        b = barre antislash    h = barre horizontale
 *   s = barre slash   v = barre verticale
 *
 * Exemples : "c1h1" = cercle épaisseur 1 + horizontale épaisseur 1
 *            "-c1"  = le même en négatif (il retire un cercle)
 *
 * Les tokens `numeric` suivent une autre grammaire : un nombre, éventuellement
 * suivi de "c" + épaisseur. Exemples : "3", "-1", "3c2".
 *
 * FUSIONNER = ADDITIONNER composante par composante. La fusion est légale si
 * chaque composante reste dans [0, 2] — au-delà, la forme n'existe pas.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ATTENTION — ce module reproduit le comportement actuel À L'IDENTIQUE, y
 * compris trois bizarreries héritées, signalées par des commentaires
 * `[COMPORTEMENT HÉRITÉ]`. Elles sont verrouillées par le golden-master. Les
 * corriger est une décision produit : ça change le scoring de candidats.
 */

/** Un token, vu par la logique de fusion. */
export type BrainToken = {
  name: string
  /** "abstract" | "numeric" en pratique, mais brain.js ne le contraint pas. */
  type: string
  /** Positionné à la création du token quand l'URL contenait "neg". */
  isNeg?: boolean
}

/** Composantes d'un token `abstract`. `null` = composante absente du nom. */
export type AbstractValues = {
  c: number | null
  b: number | null
  h: number | null
  s: number | null
  v: number | null
  isNeg: boolean
}

/** Composantes d'un token `numeric`. */
export type NumericValues = {
  c: number | null
  n: number | null
  isNeg: boolean
}

export type TokenValues = AbstractValues | NumericValues

/** Somme des tokens d'une dropzone. */
export type AbstractSum = {
  c: number
  b: number
  h: number
  s: number
  v: number
  isNeg: boolean
}

export type NumericSum = {
  c: number
  n: number
  isNeg: boolean
}

export type MergeResult = {
  /** La somme, ou "" si rien n'a pu être calculé. */
  result: AbstractSum | NumericSum | ""
  /**
   * `undefined` quand le calcul n'a pas eu lieu (dropzone vide, types
   * mélangés, type inconnu) — cf. [COMPORTEMENT HÉRITÉ] n°1.
   */
  isLegal: boolean | undefined
  /** Nom de la texture résultante, ex. "c1h2". `undefined` si non calculé. */
  texture: string | undefined
  /** Type des tokens de la dropzone. `undefined` si la dropzone est vide. */
  tokensType: string | undefined
}

/** Épaisseur maximale admise pour une composante. Au-delà, la forme n'existe pas. */
const MAX_COMPONENT = 2

/**
 * Lit une composante dans un nom de token.
 *
 * Reproduit `name.indexOf(l) > -1 ? parseInt(name[name.indexOf(l) + 1]) : null`.
 * Renvoie volontairement `NaN` si le caractère suivant la lettre n'est pas un
 * chiffre (ex. "c" seul) : c'est le comportement actuel, et le corriger
 * masquerait des données d'entrée invalides au lieu de les révéler.
 */
function readComponent(
  name: string,
  letter: string,
  negate: boolean
): number | null {
  const i = name.indexOf(letter)
  if (i === -1) return null
  const value = parseInt(name[i + 1])
  return negate ? -value : value
}

/**
 * Décompose un token en ses composantes numériques.
 *
 * Pure : contrairement à l'original qui écrivait dans `token.values`, on
 * renvoie la valeur. Vérifié sans risque — `computeTokenValues` n'avait qu'un
 * seul appelant (`calculateDropzoneValue`) et `token.values` n'était lu que là.
 *
 * Renvoie `undefined` si le type n'est ni "abstract" ni "numeric"
 * — cf. [COMPORTEMENT HÉRITÉ] n°3.
 */
export function computeTokenValues(token: BrainToken): TokenValues | undefined {
  const name = token.name
  const isNegName = name.indexOf("-") > -1

  if (token.type === "abstract") {
    return {
      c: readComponent(name, "c", isNegName),
      b: readComponent(name, "b", isNegName),
      h: readComponent(name, "h", isNegName),
      s: readComponent(name, "s", isNegName),
      v: readComponent(name, "v", isNegName),
      isNeg: isNegName,
    }
  }

  if (token.type === "numeric") {
    const ci = name.indexOf("c")
    const hasCircle = ci > -1

    let c = hasCircle ? parseInt(name[ci + 1]) : null
    // Le nombre, c'est tout ce qui précède le "c" (ou le nom entier sans "c")
    const n = hasCircle ? parseInt(name.substr(0, ci)) : parseInt(name)

    // Sur un token numeric négatif, le cercle est retiré lui aussi — sauf si
    // le token était déjà marqué `isNeg` à la création (cf. brain.js:1366).
    if (isNegName && !token.isNeg) {
      // [COMPORTEMENT HÉRITÉ] n°2 — deux choses ici :
      //  1. `-null` vaut -0, pas null : une composante absente devient donc
      //     présente et valant -0. Sans effet sur la somme (x + -0 === x),
      //     mais on le conserve pour rester strictement iso.
      //  2. brain.js posait `isNeg = true` juste ici, puis le `else` du bloc
      //     suivant l'écrasait immédiatement à `false` (else mal rattaché).
      //     Le résultat observable est donc `isNeg: false`. On le reproduit.
      c = -(c as number)
    }

    return {
      c,
      n,
      // Conséquence du else mal rattaché : seul le cas `isNegName && isNeg`
      // produit `true`.
      isNeg: isNegName && Boolean(token.isNeg),
    }
  }

  return undefined
}

/** Assemble le nom de texture d'une somme abstract, ex. "c1h2". */
function abstractTextureName(sum: AbstractSum): string {
  let name = ""
  if (sum.c !== 0) name += "c" + sum.c
  if (sum.b !== 0) name += "b" + sum.b
  if (sum.h !== 0) name += "h" + sum.h
  if (sum.s !== 0) name += "s" + sum.s
  if (sum.v !== 0) name += "v" + sum.v
  // Tout s'annule (ex. c1 + -c1) : la texture neutre
  return name === "" ? "0" : name
}

/**
 * Calcule la valeur d'une dropzone à partir des tokens qu'elle contient,
 * et dit si la fusion est légale.
 */
export function calculateDropzoneValue(
  tokens: BrainToken[] | null | undefined
): MergeResult {
  let result: AbstractSum | NumericSum | "" = ""
  let tokensType: string | undefined
  let isLegal: boolean | undefined
  let texture: string | undefined

  if (tokens && tokens.length > 0) {
    tokensType = tokens[0].type

    // Une fusion n'a de sens qu'entre tokens de même type
    const sameTypes = tokens.every((t) => t.type === tokensType)

    if (!sameTypes) {
      // [COMPORTEMENT HÉRITÉ] n°1 — brain.js:1147 lisait ici `legal` et
      // `finalTokensSumName`, deux `var` hoistées mais pas encore affectées.
      // L'appelant reçoit donc `isLegal: undefined`, qui n'est ni true ni
      // false : `if (mergeResult.isLegal)` part dans la branche « illégal »,
      // ce qui donne le bon rendu — mais par accident, pas par intention.
      return { result, isLegal, texture, tokensType }
    }

    const values = tokens.map(computeTokenValues)

    if (tokensType === "abstract") {
      const sum: AbstractSum = { c: 0, b: 0, h: 0, s: 0, v: 0, isNeg: false }

      for (const v of values as AbstractValues[]) {
        // `!= null` (et non `!== null`) : écarte aussi undefined, comme l'original
        if (v.c != null) sum.c += v.c
        if (v.b != null) sum.b += v.b
        if (v.h != null) sum.h += v.h
        if (v.s != null) sum.s += v.s
        if (v.v != null) sum.v += v.v
      }

      texture = abstractTextureName(sum)

      isLegal =
        !(sum.c > MAX_COMPONENT || sum.c < 0) &&
        !(sum.b > MAX_COMPONENT || sum.b < 0) &&
        !(sum.h > MAX_COMPONENT || sum.h < 0) &&
        !(sum.s > MAX_COMPONENT || sum.s < 0) &&
        !(sum.v > MAX_COMPONENT || sum.v < 0)

      result = sum
    }

    if (tokensType === "numeric") {
      const sum: NumericSum = { c: 0, n: 0, isNeg: false }

      for (let i = 0; i < values.length; i++) {
        const v = values[i] as NumericValues
        if (v.c != null) sum.c += v.c
        if (v.n != null) sum.n += v.n
        // Note : l'original teste `tokens[i].isNeg`, pas `values[i].isNeg`
        if (tokens[i].isNeg) sum.isNeg = true
      }

      texture = String(sum.n) + (sum.c !== 0 ? "c" + sum.c : "")

      isLegal = !(
        sum.c > MAX_COMPONENT ||
        (sum.c < 0 && !sum.isNeg) ||
        (sum.n < 0 && tokens.length === 1 && !sum.isNeg)
      )

      result = sum
    }
  }

  return { result, isLegal, texture, tokensType }
}
